/**
 * System equip / unequip — the loadout invariants (SP budget, Unique, AI).
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type ApplyCtx, type Result } from "@lancer-vtt/shared";
import { MissingContentError } from "@lancer-vtt/compendium";
import type { EquipSystemCommand, RosterCommand, UnequipSystemCommand } from "../commands";
import { isFrameIntegratedItem } from "../frameIntegratedGear";
import { licenseRequirement, meetsLicenseRequirement } from "../licenses";
import { checkLoadoutInvariants } from "../loadoutInvariants";
import { meetsTalentRequirement, talentRequirement } from "../talentGear";
import { isIntegratedTalentItem } from "../talentIntegratedGear";
import type { LoadoutData, RosterState } from "../types";
import { changed, requireCompendium, requireLoadout, requireMech, requirePilot, withLoadout, withMech, withPilot, type RosterApplyResult } from "./context";

// ── equip_system / unequip_system (loadout invariants: SP + Unique + AI) ─────────

/**
 * Equip a mech system onto a loadout: append `systemId`, then run the post-apply
 * loadout invariants on the result (`checkLoadoutInvariants`) and hard-reject on a
 * violation — the **SP-budget** tenant (the resulting SP-used must fit the mech's
 * budget, via the shared `sp.ts` helpers `compute.ts` also displays), the **Unique**
 * tenant (no 2nd copy of a `tg_unique` system), and the **AI** tenant (no 2nd `tg_ai`
 * system). Reference-dependent — resolves the system against `ctx.compendium` for its
 * license/`sp`/`tags` (an unknown id is a structured `MissingContentError`). Mirrors
 * the build-`nextMech`-then-check structure of the budget gates. The inverse is a
 * symmetric `unequip_system` (ids recoverable, no lossy capture).
 */
export function applyEquipSystem(
  state: RosterState,
  cmd: EquipSystemCommand,
  ctx: ApplyCtx,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const loadoutR = requireLoadout(mech, cmd.loadoutId);
  if (!loadoutR.ok) return loadoutR;
  const loadout = loadoutR.value;
  const compendiumR = requireCompendium(ctx);
  if (!compendiumR.ok) return compendiumR;
  const compendium = compendiumR.value;

  // Resolve the system (reference-dependent): an unknown id is structured missing
  // content, a `throw` (not the `Result` channel), like `equip_weapon`'s weapon.
  const system = compendium.getById("system", cmd.systemId);
  if (!system) {
    throw new MissingContentError("system", cmd.systemId, `mech ${cmd.mechId}`);
  }

  // License gate (single-source `licenses.ts` helper): hard-reject a system whose
  // required license rank the pilot hasn't earned, before the SP-budget gate. GMS
  // systems pass via the seeded rank; unlicensed/rank-0 systems are always allowed.
  if (!meetsLicenseRequirement(pilot, system, "system")) {
    const req = licenseRequirement(system, "system");
    return err(
      new CommandError(
        `equip_system: system ${cmd.systemId} requires license ${req?.id} rank ${req?.level}; pilot has not earned it`,
      ),
    );
  }

  // Talent gate (single-source `talentGear.ts` helper): hard-reject a system
  // whose granting talent rank the pilot hasn't earned (e.g. `ms_technophile_1`
  // needs Technophile r1). GMS license_level 0 lets these pass the license gate
  // above, so this is the gap-closing gate. Items with no `talent_id` pass.
  if (!meetsTalentRequirement(pilot, system)) {
    const req = talentRequirement(system);
    return err(
      new CommandError(
        `equip_system: system ${cmd.systemId} requires talent ${req?.id} rank ${req?.rank}; pilot has not earned it`,
      ),
    );
  }

  // Talent-**integrated** gate (`talentIntegratedGear.ts`'s manual-equip block, single-source
  // with the kit affordance, mirroring `applyEquipWeapon`'s twin): hard-reject manually equipping
  // a system a talent auto-grants (e.g. `ms_walking_armory_1` auto-arms at Walking Armory r1) —
  // it's slot/SP-exempt and already armed at spawn (`spawn.ts`'s `freezeSystems` talent-integrated
  // pass), so a manual equip would double-grant it and (RAW-wrongly) consume real SP.
  // `special_equipment` (Technophile NHPs, `ms_technophile_1/2/3`) is NOT integrated and stays
  // manually equippable — this predicate keys strictly on the talent's `integrated[]` array,
  // never catching an NHP.
  if (isIntegratedTalentItem(system, compendium)) {
    return err(
      new CommandError(
        `equip_system: system ${cmd.systemId} is talent-integrated (auto-granted at spawn); it cannot be manually equipped`,
      ),
    );
  }

  // Frame-**integrated** gate (`frameIntegratedGear.ts`, `applyEquipWeapon`'s twin): the same
  // wall for a system a *frame* hard-mounts. Core lancer-data frames only integrate weapons, but
  // the `frame.integrated[]` field is kind-agnostic and an LCP may name a system — gate both
  // sides so the rule doesn't have a hole shaped like a content pack.
  if (isFrameIntegratedItem(cmd.systemId, compendium)) {
    return err(
      new CommandError(
        `equip_system: system ${cmd.systemId} is frame-integrated (hard-mounted by its chassis); it cannot be manually equipped`,
      ),
    );
  }

  const nextLoadout: LoadoutData = {
    ...loadout,
    systems: [...(loadout.systems ?? []), cmd.systemId],
  };
  const nextMech = withLoadout(mech, nextLoadout);

  // Post-apply loadout invariants (the SP-budget tenant folds in the former inline SP
  // check; Unique + AI both bite here — a 2nd copy of a `tg_unique` system, or a 2nd
  // `tg_ai` system). Build `nextMech` first, then check the result.
  const violation = checkLoadoutInvariants(nextMech, pilot, compendium);
  if (violation) {
    return err(new CommandError(`equip_system: ${violation.message}`));
  }

  const nextPilot = withMech(pilot, nextMech);
  return ok({
    state: withPilot(state, nextPilot),
    inverse: {
      type: "unequip_system",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      mechId: cmd.mechId,
      loadoutId: cmd.loadoutId,
      systemId: cmd.systemId,
    },
    events: changed(cmd.pilotId),
  });
}

/**
 * Remove a mech system from a loadout: drop the **last** occurrence of `systemId`.
 * Raw-only (removal never needs a compendium and never overflows budget). Last (not
 * first) is the exact inverse of `equip_system`, which always *appends*: undoing an
 * equip removes precisely the copy it added, so `equip ↔ unequip` round-trips even if
 * the id already appeared earlier in the list. The inverse re-equips the same id when
 * one was actually removed (re-append restores the list); if the id wasn't present,
 * the inverse is another `unequip_system` — a no-op round-trip.
 */
export function applyUnequipSystem(
  state: RosterState,
  cmd: UnequipSystemCommand,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const mechR = requireMech(pilot, cmd.mechId);
  if (!mechR.ok) return mechR;
  const mech = mechR.value;
  const loadoutR = requireLoadout(mech, cmd.loadoutId);
  if (!loadoutR.ok) return loadoutR;
  const loadout = loadoutR.value;

  const systems = loadout.systems ?? [];
  const idx = systems.lastIndexOf(cmd.systemId);
  const present = idx !== -1;
  const nextSystems = present
    ? [...systems.slice(0, idx), ...systems.slice(idx + 1)]
    : systems;
  const nextLoadout: LoadoutData = { ...loadout, systems: nextSystems };
  const nextMech = withLoadout(mech, nextLoadout);
  const nextPilot = withMech(pilot, nextMech);

  // Inverse: re-equip the removed system (restores it), or — if it wasn't there —
  // unequip again (a no-op round-trip). Symmetric, no lossy capture.
  const inverse: RosterCommand = present
    ? {
        type: "equip_system",
        actorId: cmd.actorId,
        pilotId: cmd.pilotId,
        mechId: cmd.mechId,
        loadoutId: cmd.loadoutId,
        systemId: cmd.systemId,
      }
    : {
        type: "unequip_system",
        actorId: cmd.actorId,
        pilotId: cmd.pilotId,
        mechId: cmd.mechId,
        loadoutId: cmd.loadoutId,
        systemId: cmd.systemId,
      };

  return ok({
    state: withPilot(state, nextPilot),
    inverse,
    events: changed(cmd.pilotId),
  });
}
